/* eslint-disable indent, @typescript-eslint/indent, react/jsx-indent, react/jsx-indent-props */
import React from 'react';
import ZoomVideo, { VideoPlayer, VideoQuality, Stream, MediaDevice } from '@zoom/videosdk';
import s from './s.module.less';
import { TDeviceInfo } from 'types/zoom';
import { Button, Modal, Select, Spin, message } from 'antd';

/**
 * 设置所有 media 元素（video/audio）的音频输出设备
 * 某些浏览器需要在 HTML 元素层面也设置 sinkId 才能生效
 */
const setAudioOutputForAllMediaElements = async (deviceId: string): Promise<void> => {
    const promises: Promise<void>[] = [];

    // 查找所有标准的 video 和 audio 元素
    const videoElements = document.querySelectorAll('video');
    const audioElements = document.querySelectorAll('audio');

    // 查找 Zoom SDK 的 video-player 元素内部的 video 元素
    const zoomVideoPlayers = document.querySelectorAll('video-player');

    // 处理标准 video 元素
    videoElements.forEach((video) => {
        // 检查是否支持 setSinkId（使用类型断言，因为 TypeScript 可能没有这个类型定义）
        const videoEl = video as HTMLVideoElement & {
            setSinkId?: (deviceId: string) => Promise<void>;
            webkitSetSinkId?: (deviceId: string) => Promise<void>;
        };
        if (typeof videoEl.setSinkId === 'function') {
            promises.push(
                videoEl.setSinkId(deviceId).catch((err: Error) => {
                    console.warn('设置 video 元素音频输出失败:', err);
                }),
            );
        } else if (typeof videoEl.webkitSetSinkId === 'function') {
            // Safari 可能使用 webkit 前缀
            promises.push(
                videoEl.webkitSetSinkId(deviceId).catch((err: Error) => {
                    console.warn('设置 video 元素音频输出失败 (webkit):', err);
                }),
            );
        }
    });

    // 处理标准 audio 元素
    audioElements.forEach((audio) => {
        const audioEl = audio as HTMLAudioElement & {
            setSinkId?: (deviceId: string) => Promise<void>;
            webkitSetSinkId?: (deviceId: string) => Promise<void>;
        };
        if (typeof audioEl.setSinkId === 'function') {
            promises.push(
                audioEl.setSinkId(deviceId).catch((err: Error) => {
                    console.warn('设置 audio 元素音频输出失败:', err);
                }),
            );
        } else if (typeof audioEl.webkitSetSinkId === 'function') {
            promises.push(
                audioEl.webkitSetSinkId(deviceId).catch((err: Error) => {
                    console.warn('设置 audio 元素音频输出失败 (webkit):', err);
                }),
            );
        }
    });

    // 处理 Zoom SDK 的 video-player 元素
    // 这些元素内部有 shadow DOM 或实际的 video 元素
    zoomVideoPlayers.forEach((player) => {
        // 尝试查找内部的 video 元素（通过 shadow DOM）
        const { shadowRoot } = player as Element & { shadowRoot?: ShadowRoot };
        if (shadowRoot) {
            const internalVideos = shadowRoot.querySelectorAll('video');
            internalVideos.forEach((video) => {
                const videoEl = video as HTMLVideoElement & {
                    setSinkId?: (deviceId: string) => Promise<void>;
                    webkitSetSinkId?: (deviceId: string) => Promise<void>;
                };
                if (typeof videoEl.setSinkId === 'function') {
                    promises.push(
                        videoEl.setSinkId(deviceId).catch((err: Error) => {
                            console.warn('设置 Zoom video-player 内部 video 元素音频输出失败:', err);
                        }),
                    );
                } else if (typeof videoEl.webkitSetSinkId === 'function') {
                    promises.push(
                        videoEl.webkitSetSinkId(deviceId).catch((err: Error) => {
                            console.warn('设置 Zoom video-player 内部 video 元素音频输出失败 (webkit):', err);
                        }),
                    );
                }
            });
        }

        // 也尝试直接查找子元素中的 video（某些情况下可能没有 shadow DOM）
        const childVideos = player.querySelectorAll('video');
        childVideos.forEach((video) => {
            const videoEl = video as HTMLVideoElement & {
                setSinkId?: (deviceId: string) => Promise<void>;
                webkitSetSinkId?: (deviceId: string) => Promise<void>;
            };
            if (typeof videoEl.setSinkId === 'function') {
                promises.push(
                    videoEl.setSinkId(deviceId).catch((err: Error) => {
                        console.warn('设置 Zoom video-player 子 video 元素音频输出失败:', err);
                    }),
                );
            } else if (typeof videoEl.webkitSetSinkId === 'function') {
                promises.push(
                    videoEl.webkitSetSinkId(deviceId).catch((err: Error) => {
                        console.warn('设置 Zoom video-player 子 video 元素音频输出失败 (webkit):', err);
                    }),
                );
            }
        });
    });

    if (promises.length > 0) {
        await Promise.all(promises);
        console.log(`已尝试设置 ${promises.length} 个 media 元素的音频输出设备`);
    } else {
        console.warn('未找到支持 setSinkId 的 media 元素，或浏览器不支持此 API');
    }
};

type Props = {
    zoomMediaStream?: typeof Stream | null;
    zoomClient?: ReturnType<typeof ZoomVideo.createClient> | null;
    open?: boolean;
    onCancel?: () => void;
    onSpeakerChange?: (deviceId: string) => void;
    onMicChange?: (deviceId: string) => void;
    onCameraChange?: (deviceId: string) => void;
};

// Zoom SDK 的 Part
const VideoSettingModal = (props: Props) => {
    const { zoomMediaStream, zoomClient, open, onCancel, onSpeakerChange, onMicChange, onCameraChange } = props;
    const [micList, setMicList] = React.useState<TDeviceInfo[]>([]);
    const [selectedMicDeviceId, setSelectedMicDeviceId] = React.useState<string | null>(null);
    const [speakerList, setSpeakerList] = React.useState<TDeviceInfo[]>([]);
    const [selectedSpeakerDeviceId, setSelectedSpeakerDeviceId] = React.useState<string | null>(null);
    const [cameraList, setCameraList] = React.useState<TDeviceInfo[]>([]);
    const [selectedCameraDeviceId, setSelectedCameraDeviceId] = React.useState<string | null>(null);
    React.useEffect(() => {
        const getDeviceLists = async () => {
            if (!open || !zoomMediaStream) return;

            try {
                // Zoom SDK 的 getMicList() 和 getSpeakerList() 需要在 startAudio() 之后才能返回设备列表

                const _micList = zoomMediaStream.getMicList();
                const _speakerList = zoomMediaStream.getSpeakerList();
                const _cameraList = zoomMediaStream.getCameraList();
                const _activeMic = zoomMediaStream.getActiveMicrophone();
                const _activeSpeaker = zoomMediaStream.getActiveSpeaker();
                const _activeCamera = zoomMediaStream.getActiveCamera();

                console.log({ _micList, _speakerList, _cameraList, _activeMic, _activeSpeaker, _activeCamera });
                // 转换为 TDeviceInfo 格式
                const micsList: TDeviceInfo[] = [];
                const micsDeviceIds: string[] = ['default'];
                const defaultMic: MediaDevice | undefined = _micList.find((device) => device.deviceId === 'default');

                if (defaultMic) {
                    const defaultMicWithDeviceId: MediaDevice | undefined = _micList.find((device) => {
                        return defaultMic.label.includes(device.label) && device.deviceId !== 'default';
                    });
                    if (defaultMicWithDeviceId) {
                        micsList.push({
                            deviceId: defaultMicWithDeviceId.deviceId,
                            label: defaultMicWithDeviceId.label,
                            isDefault: true,
                        });
                        micsDeviceIds.push(defaultMicWithDeviceId.deviceId);
                        setSelectedMicDeviceId(defaultMicWithDeviceId.deviceId);
                    }
                }
                _micList.forEach((device) => {
                    if (!micsDeviceIds.includes(device.deviceId)) {
                        micsList.push({
                            deviceId: device.deviceId,
                            label: device.label || `Microphone ${device.deviceId.slice(0, 8)}`,
                            isDefault: false,
                        });
                    }
                });

                const speakersList: TDeviceInfo[] = [];
                const speakersDeviceIds: string[] = ['default'];
                const defaultSpeaker: MediaDevice | undefined = _speakerList.find(
                    (device) => device.deviceId === 'default',
                );
                if (defaultSpeaker) {
                    const defaultSpeakerWithDeviceId: MediaDevice | undefined = _speakerList.find((device) => {
                        return defaultSpeaker.label.includes(device.label) && device.deviceId !== 'default';
                    });
                    if (defaultSpeakerWithDeviceId) {
                        speakersList.push({
                            deviceId: defaultSpeakerWithDeviceId.deviceId,
                            label: defaultSpeakerWithDeviceId.label,
                            isDefault: true,
                        });
                        speakersDeviceIds.push(defaultSpeakerWithDeviceId.deviceId);
                        setSelectedSpeakerDeviceId(defaultSpeakerWithDeviceId.deviceId);
                    }
                }
                _speakerList.forEach((device) => {
                    if (!speakersDeviceIds.includes(device.deviceId)) {
                        speakersList.push({
                            deviceId: device.deviceId,
                            label: device.label || `Speaker ${device.deviceId.slice(0, 8)}`,
                            isDefault: false,
                        });
                    }
                });

                const camerasList: TDeviceInfo[] = [];
                const camerasDeviceIds: string[] = ['default'];
                const defaultCamera: MediaDevice | undefined = _cameraList.find(
                    (device) => device.deviceId === 'default',
                );
                if (defaultCamera) {
                    const defaultCameraWithDeviceId: MediaDevice | undefined = _cameraList.find((device) => {
                        return defaultCamera.label.includes(device.label) && device.deviceId !== 'default';
                    });
                    if (defaultCameraWithDeviceId) {
                        camerasList.push({
                            deviceId: defaultCameraWithDeviceId.deviceId,
                            label: defaultCameraWithDeviceId.label,
                            isDefault: true,
                        });
                        camerasDeviceIds.push(defaultCameraWithDeviceId.deviceId);
                        setSelectedCameraDeviceId(defaultCameraWithDeviceId.deviceId);
                    }
                }
                _cameraList.forEach((device) => {
                    if (!camerasDeviceIds.includes(device.deviceId)) {
                        camerasList.push({
                            deviceId: device.deviceId,
                            label: device.label || `Camera ${device.deviceId.slice(0, 8)}`,
                            isDefault: false,
                        });
                        camerasDeviceIds.push(device.deviceId);
                        setSelectedCameraDeviceId(device.deviceId);
                    }
                });

                if (micsList && micsList.length === 0) {
                    micsList[0].isDefault = true;
                    setSelectedMicDeviceId(micsList[0].deviceId);
                }

                if (speakersList && speakersList.length === 0) {
                    speakersList[0].isDefault = true;
                    setSelectedSpeakerDeviceId(speakersList[0].deviceId);
                }

                if (camerasList && camerasList.length === 0) {
                    camerasList[0].isDefault = true;
                    setSelectedCameraDeviceId(camerasList[0].deviceId);
                }

                setMicList(micsList);
                setSpeakerList(speakersList);
                setCameraList(camerasList);
            } catch (error) {
                console.error('获取音频设备列表失败:', error);
            }
        };
        getDeviceLists();
    }, [open, zoomMediaStream]);

    return (
        <Modal open={open} onCancel={onCancel} width={450} title="Video settings" footer={null}>
            <div className={s.wrap}>
                <div className={s.label}>Speaker</div>
                <div className={s.option}>
                    <Select
                        value={selectedSpeakerDeviceId}
                        onChange={async (value) => {
                            if (!zoomMediaStream) {
                                message.error('Media is not available. Please check your device connection.');
                                return;
                            }

                            try {
                                // 切换扬声器
                                const result = await zoomMediaStream.switchSpeaker(value);

                                // ExecutedResult 返回空字符串表示成功，返回 ExecutedFailure 表示失败
                                if (result === '') {
                                    console.log(`切换扬声器成功: ${value}`);

                                    // 验证切换是否真的生效（getActiveSpeaker 返回设备 ID 字符串）
                                    const activeSpeakerId = zoomMediaStream.getActiveSpeaker();
                                    if (activeSpeakerId === value) {
                                        // 尝试设置所有 video/audio 元素的音频输出设备
                                        // 因为某些浏览器需要同时设置 HTML 元素的 sinkId
                                        try {
                                            await setAudioOutputForAllMediaElements(value);
                                        } catch (sinkError) {
                                            console.warn('设置 HTML 元素音频输出失败（可能不支持）:', sinkError);
                                            // 这个错误不影响，因为 SDK 已经切换了
                                        }

                                        setSelectedSpeakerDeviceId(value);
                                        message.success('Speaker switched successfully');
                                    } else {
                                        console.warn('切换扬声器后验证失败，当前活跃设备:', activeSpeakerId);
                                        // 即使验证失败，也更新 UI，因为可能在某些浏览器上验证不准确
                                        //setSelectedSpeakerDeviceId(value);
                                        //message.warning('扬声器可能已切换，请检查音频输出');
                                    }
                                } else {
                                    // result 是 ExecutedFailure 对象
                                    const error = result as { type?: string; reason?: string; errorCode?: number };
                                    console.error('Speaker switch failed:', error);
                                    message.error(`Switch failed: ${error.reason || error.type || 'Unknown error'}`);
                                }
                            } catch (error) {
                                const err = error as { message?: string; reason?: string; type?: string };
                                console.error('Speaker switch exception:', err);
                                message.error(`Switch failed: ${err?.message || err?.reason || 'Unknown error'}`);
                            }

                            onSpeakerChange?.(value);
                        }}
                        style={{ width: '100%' }}
                    >
                        {speakerList.map((item) => (
                            <Select.Option key={item.deviceId} value={item.deviceId}>
                                {item.label} {item?.isDefault ? ' (Default)' : ''}
                            </Select.Option>
                        ))}
                    </Select>
                </div>
                <div className={s.label} style={{ marginTop: 24 }}>
                    Microphone
                </div>
                <div className={s.option}>
                    <Select
                        value={selectedMicDeviceId}
                        onChange={async (value) => {
                            if (!zoomMediaStream) {
                                message.error('Media stream not initialized');
                                return;
                            }

                            try {
                                // 切换麦克风
                                const result = await zoomMediaStream.switchMicrophone(value);

                                // ExecutedResult 返回空字符串表示成功，返回 ExecutedFailure 表示失败
                                if (result === '') {
                                    console.log(`切换麦克风成功: ${value}`);

                                    // 验证切换是否真的生效（getActiveMicrophone 返回设备 ID 字符串）
                                    const activeMicId = zoomMediaStream.getActiveMicrophone();
                                    if (activeMicId === value) {
                                        setSelectedMicDeviceId(value);
                                        message.success('Microphone switched successfully');
                                    } else {
                                        console.warn('切换麦克风后验证失败，当前活跃设备:', activeMicId);
                                        // 即使验证失败，也更新 UI，因为可能在某些浏览器上验证不准确
                                        //setSelectedMicDeviceId(value);
                                        message.warning(
                                            'Microphone may have been switched, please check the audio input',
                                        );
                                    }
                                } else {
                                    // result 是 ExecutedFailure 对象
                                    const error = result as { type?: string; reason?: string; errorCode?: number };
                                    console.error('Microphone switch failed:', error);
                                    message.error(`Switch failed: ${error.reason || error.type || 'Unknown error'}`);
                                }
                            } catch (error) {
                                const err = error as { message?: string; reason?: string; type?: string };
                                console.error('Microphone switch exception:', err);
                                message.error(`Switch failed: ${err?.message || err?.reason || 'Unknown error'}`);
                            }

                            onMicChange?.(value);
                        }}
                        style={{ width: '100%' }}
                    >
                        {micList.map((item) => (
                            <Select.Option key={item.deviceId} value={item.deviceId}>
                                {item.label} {item?.isDefault ? ' (Default)' : ''}
                            </Select.Option>
                        ))}
                    </Select>
                </div>
                <div className={s.label} style={{ marginTop: 24 }}>
                    Camera
                </div>
                <div className={s.option}>
                    <Select
                        value={selectedCameraDeviceId}
                        onChange={async (value) => {
                            if (!zoomMediaStream) {
                                message.error('Media stream not initialized');
                                return;
                            }

                            setSelectedCameraDeviceId(value);
                            try {
                                await zoomMediaStream?.switchCamera(value);
                                message.success('Camera switched successfully');
                            } catch (error) {
                                const err = error as { message?: string; reason?: string; type?: string };
                                console.error('Camera switch exception:', err);
                                message.error(`Switch failed: ${err?.message || err?.reason || 'Unknown error'}`);
                            }
                            onCameraChange?.(value);
                        }}
                        style={{ width: '100%' }}
                    >
                        {cameraList.map((item) => (
                            <Select.Option key={item.deviceId} value={item.deviceId}>
                                {item.label} {item?.isDefault ? ' (Default)' : ''}
                            </Select.Option>
                        ))}
                    </Select>
                </div>
            </div>
        </Modal>
    );
};

export default VideoSettingModal;
